import React, { useState, useEffect, useRef } from 'react';
import {
    UserIcon,
    PencilIcon,
    TrashIcon,
    IdentificationIcon,
    EllipsisVerticalIcon,
    XMarkIcon,
    EyeIcon,
    FolderIcon,
    ArrowDownIcon,
    Bars3Icon,
    FunnelIcon,
    ChevronDownIcon,
    Squares2X2Icon,
    ListBulletIcon,
    MagnifyingGlassIcon,
    EnvelopeIcon,
    PhoneIcon,
    EllipsisHorizontalIcon,
    CreditCardIcon,
    ClipboardDocumentIcon,
    DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { PersonalDetail } from '../../types/personalDetails';
import { toast } from 'react-hot-toast';
import PersonalDetailsService from '../../services/personalDetails.service';
import { useVault } from '../../contexts/VaultContext';
import { useAppSelector } from '../../hooks/useRedux';
import ConfirmModal from '../common/ConfirmModal';
import { useCategories } from '../../contexts/CategoryContext';

interface PersonalDetailsListProps {
    details: PersonalDetail[];
    onUpdate: () => void;
    onEdit: (detail: PersonalDetail, isShared?: boolean, sharedKey?: string) => void;
    hideFavoriteIcon?: boolean;
    isAdminView?: boolean
}

const renderCategories = (entryCategories: any[], categories?: any[]) => {
    if (!entryCategories || entryCategories.length === 0) return null;

    const validCategories = entryCategories.filter(entryCategory =>
        categories?.some((userCategory: any) => userCategory.id === entryCategory.id)
    );

    if (validCategories.length === 0) return null;

    return (
        <>
            {validCategories.slice(0, 2).map((category, index) => (
                <React.Fragment key={category.id}>
                    <div className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs
                        ${category.is_primary
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                        <FolderIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate max-w-20">{category.name}</span>
                    </div>
                </React.Fragment>
            ))}
            {validCategories.length > 2 && (
                <div className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                    +{validCategories.length - 2} more
                </div>
            )}
        </>
    );
};

// Helper function to get stored view mode preference
const getStoredPersonalViewMode = (): 'grid' | 'list' => {
    try {
        const stored = localStorage.getItem('personalDetailsViewMode');
        if (stored === 'grid' || stored === 'list') {
            return stored;
        }
    } catch (error) {
        console.warn('Failed to read personal details view mode preference from localStorage:', error);
    }
    return 'grid'; // Default fallback
};

// Helper function to store view mode preference
const storePersonalViewMode = (mode: 'grid' | 'list') => {
    try {
        localStorage.setItem('personalDetailsViewMode', mode);
    } catch (error) {
        console.warn('Failed to save personal details view mode preference to localStorage:', error);
    }
};

const PersonalDetailsList: React.FC<PersonalDetailsListProps> = ({ details, onUpdate, onEdit, hideFavoriteIcon = false, isAdminView = false }) => {
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const [activeCard, setActiveCard] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const { searchQuery, setSearchQuery } = useVault();
    const { role_id, team_id } = useAppSelector(state => state.auth);
    const { categories } = useCategories();

    // View mode and filter states - Initialize with stored preference
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => getStoredPersonalViewMode());
    const [selectedPersonal, setSelectedPersonal] = useState<PersonalDetail | null>(null);

    // Enhanced setViewMode that persists the preference
    const handleViewModeChange = (mode: 'grid' | 'list') => {
        setViewMode(mode);
        storePersonalViewMode(mode);
    };
    const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'alphabetical'>('newest');
    const [showSharedOnly, setShowSharedOnly] = useState<boolean>(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
    const [tempShowSharedOnly, setTempShowSharedOnly] = useState<boolean>(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    // Confirm modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [detailToDelete, setDetailToDelete] = useState<string>('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    // Detect screen size changes for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDeleteClick = (id: string, is_owner: boolean) => {
        setDetailToDelete(id);
        setIsOwner(is_owner);
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            await PersonalDetailsService.deletePersonalDetail(detailToDelete, !isOwner);
            toast.success('Personal detail deleted successfully');
            onUpdate();
            setActiveCard(null);
        } catch (error) {
            toast.error('Failed to delete personal detail');
        } finally {
            setIsDeleting(false);
            setShowConfirmModal(false);
        }
    };

    const handleEdit = async (entryId: string, isShared?: boolean, sharedKey?: string) => {
        try {
            if (isAdminView) {
                // Admin view: Use already-decrypted details from props
                const detail = details.find((d) => d.id === entryId);
                if (!detail) {
                    toast.error('Bank Details not found');
                    return;
                }
                // Force read-only mode for admin
                onEdit({ ...detail, permission_level: 'read' }, isShared ?? false, detail.shared_key ?? sharedKey ?? '');
                setActiveCard(null);
                return;
            }
            const detail = await PersonalDetailsService.getPersonalDetailById(entryId, isShared, sharedKey);
            console.log('detail', detail);
            if (!detail.is_owner) {
                const PersonalDetails = details.filter((entryId) => entryId.id === detail.id);
                detail.is_shared = PersonalDetails[0].is_shared;
                detail.shared_key = PersonalDetails[0].shared_key;
                detail.permission_level = PersonalDetails[0].permission_level;
            }
            onEdit(detail, isShared ?? false, detail.shared_key ?? sharedKey ?? '');
            setActiveCard(null);
        } catch (err) {
            console.error('Failed to fetch entry:', err);
            toast.error('Failed to fetch entry');
        }
    };

    const handleClone = async (detail: PersonalDetail) => {
        try {
            if (!isAdminView) {
                toast.error('Clone feature is only available in admin view');
                return;
            }

            const confirmed = window.confirm('Are you sure you want to clone this personal detail to your vault?');
            if (!confirmed) return;

            // Create new personal detail object with all fields
            const newPersonalDetail: any = {
                name: detail.name,
                title: detail.title,
                encrypted_first_name: detail.encrypted_first_name,
                encrypted_middle_name: detail.encrypted_middle_name,
                encrypted_last_name: detail.encrypted_last_name,
                encrypted_username: detail.encrypted_username,
                gender: detail.gender,
                birthday: detail.birthday,
                encrypted_email_address: detail.encrypted_email_address,
                encrypted_phone: detail.encrypted_phone,
                encrypted_address_1: detail.encrypted_address_1,
                encrypted_address_2: detail.encrypted_address_2,
                encrypted_city_town: detail.encrypted_city_town,
                encrypted_state: detail.encrypted_state,
                encrypted_zip_postal_code: detail.encrypted_zip_postal_code,
                encrypted_country: detail.encrypted_country,
                encrypted_timezone: detail.encrypted_timezone,
                encrypted_notes: detail.encrypted_notes ? detail.encrypted_notes + '\n\n[Cloned from user\'s vault]' : '[Cloned from user\'s vault]',
                favourite: false,
                workspace_id: team_id || undefined
            };

            // The service will automatically encrypt with the admin's vault key
            await PersonalDetailsService.createPersonalDetail(newPersonalDetail);

            toast.success('Personal detail cloned successfully!');
            onUpdate(); // Refresh the list
        } catch (err) {
            console.error('Failed to clone personal detail:', err);
            toast.error('Failed to clone personal detail');
        }
    };

    // Toggle active card on mobile
    const toggleCardOptions = (id: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent bubbling
        if (isMobile) {
            setActiveCard(activeCard === id ? null : id);
        }
    };

    // Close card options when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (activeCard) {
                setActiveCard(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeCard]);

    // Click outside handler for menus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setIsSortMenuOpen(false);
            }

            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
                setIsFilterMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cardColor = 'from-green-400 to-green-600';

    // Filter and sort personal details
    const filteredPersonalDetails = details.filter((detail: PersonalDetail) => {
        // Apply search filter
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            if (!(
                detail.name?.toLowerCase().includes(searchLower) ||
                detail.encrypted_first_name?.toLowerCase().includes(searchLower) ||
                detail.encrypted_last_name?.toLowerCase().includes(searchLower) ||
                detail.encrypted_email_address?.toLowerCase().includes(searchLower) ||
                detail.encrypted_phone?.toLowerCase().includes(searchLower)
            )) {
                return false;
            }
        }

        // Apply shared filter
        if (showSharedOnly && detail.is_owner) {
            return false;
        }

        return true;
    });

    // Sort personal details
    const sortedPersonalDetails = [...filteredPersonalDetails].sort((a, b) => {
        switch (sortOption) {
            case 'alphabetical':
                return (a.name || '').localeCompare(b.name || '');
            case 'oldest':
                return new Date(a.updated_at || a.created_at || '').getTime() - new Date(b.updated_at || b.created_at || '').getTime();
            case 'newest':
            default:
                return new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime();
        }
    });

    // Apply filter settings from temporary state to active state
    const applyFilters = () => {
        setShowSharedOnly(tempShowSharedOnly);
        setIsFilterMenuOpen(false);
    };

    // Reset temporary filter settings
    const resetFilters = () => {
        setShowSharedOnly(false);
        setTempShowSharedOnly(false);
    };

    // Auto-select first personal detail in list view
    useEffect(() => {
        if (viewMode === 'list' && sortedPersonalDetails.length > 0 && !selectedPersonal) {
            setSelectedPersonal(sortedPersonalDetails[0]);
        }
    }, [viewMode, sortedPersonalDetails, selectedPersonal]);

    // Handle copy to clipboard
    const handleCopyItem = (label: string, value: string) => {
        if (value) {
            navigator.clipboard.writeText(value);
            toast.success(`${label} copied to clipboard`);
        }
    };

    // Sort menu component
    const SortMenu = () => {
        return (
            <div className="relative z-50" ref={sortMenuRef}>
                <button
                    onClick={() => {
                        setIsSortMenuOpen(!isSortMenuOpen);
                        setIsFilterMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <ArrowDownIcon className="h-4 w-4 mr-1" />
                    Sort
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isSortMenuOpen && (
                    <div className="absolute mt-2 left-0 sm:right-0 sm:left-auto w-48 max-w-[90vw] bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="py-1">
                            {/* Newest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "newest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("newest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Newest First</span>
                                {sortOption === "newest" && <ArrowDownIcon className="h-4 w-4" />}
                            </button>

                            {/* Oldest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "oldest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("oldest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Oldest First</span>
                                {sortOption === "oldest" && <ArrowDownIcon className="h-4 w-4" />}
                            </button>

                            {/* Alphabetical */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "alphabetical"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("alphabetical");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Alphabetical</span>
                                {sortOption === "alphabetical" && (
                                    <ArrowDownIcon className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );

    };

    // Filter menu component
    const FilterMenu = () => {
        return (
            <div className="relative z-50" ref={filterMenuRef}>
                <button
                    onClick={() => {
                        setIsFilterMenuOpen(!isFilterMenuOpen);
                        setIsSortMenuOpen(false);
                        setTempShowSharedOnly(showSharedOnly);
                    }}
                    className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <FunnelIcon className="h-4 w-4 mr-1" />
                    Filter
                    {showSharedOnly && (
                        <span className="ml-1 px-1.5 py-0.5 bg-white/30 text-white text-xs rounded-full">
                            1
                        </span>
                    )}
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isFilterMenuOpen && (
                    <div className="absolute mt-2 right-0 w-64 bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Filter personal details</span>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    resetFilters();
                                }}
                                className="text-sm text-primary-600 hover:text-primary-800"
                            >
                                Reset
                            </button>
                        </div>

                        {/* Filter Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        id="sharedOnly"
                                        type="checkbox"
                                        checked={tempShowSharedOnly}
                                        onChange={(e) => setTempShowSharedOnly(e.target.checked)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="sharedOnly" className="ml-2 block text-sm text-gray-700">
                                        Shared with me only
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="p-3 border-t border-gray-200">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    applyFilters();
                                }}
                                className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderCategoryBadges = (categories: any[], badgeClassName: string, moreClassName: string) => {
        if (!categories || categories.length === 0) return null;

        return (
            <div className="flex items-center space-x-2">
                {categories.slice(0, 2).map(category => (
                    <span key={category.id} className={badgeClassName}>
                        {category.name}
                    </span>
                ))}
                {categories.length > 2 && (
                    <span className={moreClassName}>
                        +{categories.length - 2} more
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header Controls */}
            <div className="sticky top-0 z-40 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 backdrop-blur-sm border-b border-primary-300 dark:border-primary-700 px-4 sm:px-6 py-6 shadow-lg">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-32 bg-white rounded-full -translate-y-16 translate-x-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-24 bg-white rounded-full translate-y-12 -translate-x-24"></div>
                </div>

                <div className="relative flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-6 mb-4 sm:mb-0">
                        {/* Title and Icon for Desktop */}
                        <div className="hidden sm:flex items-center space-x-3">
                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
                                <UserIcon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">Personal Details</h1>
                                <p className="text-white/70 text-sm">Manage your personal information</p>
                            </div>
                        </div>

                        {/* Mobile Header */}
                        <div className="sm:hidden">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                                    <UserIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Personal Details</h1>
                                    <p className="text-white/70 text-xs">Manage your personal information</p>
                                </div>
                            </div>
                        </div>

                        {/* Count Display */}
                        <div className="flex items-center space-x-3 px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0 min-w-[160px]">
                            <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                                <IdentificationIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div>
                                <div className="text-lg sm:text-2xl font-bold text-white">
                                    {sortedPersonalDetails.length}
                                </div>
                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                    {sortedPersonalDetails.length === 1 ? 'Record' : 'Records'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:justify-end">
                        {/* Sort Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <SortMenu />
                        </div>

                        {/* Filter Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <FilterMenu />
                        </div>

                        {/* View Toggle */}
                        <div className="hidden sm:flex items-center bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 shadow-sm border border-white/30 flex-shrink-0">
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="Grid View"
                            >
                                <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="List View"
                            >
                                <ListBulletIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {sortedPersonalDetails.length === 0 && (
                <div className="text-center py-16 px-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                              rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                        <UserIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No personal details found</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {searchQuery || showSharedOnly
                            ? "No personal details match your search criteria. Try adjusting your search terms or filters."
                            : "Get started by adding your first personal detail to securely manage your information."
                        }
                    </p>
                </div>
            )}

            {sortedPersonalDetails.length > 0 && (
                <>
                    {/* Main Content */}
                    <div className="p-6">
                        {viewMode === 'grid' ? (
                            /* Grid View - Mobile Responsive */
                            <div className="personal-details-section grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 p-2 sm:p-4 md:p-6">
                                {sortedPersonalDetails.map((detail) => {
                                    const fullName = `${detail.title || ''} ${detail.encrypted_first_name || ''} ${detail.encrypted_last_name || ''}`.trim();

                                    return (
                                        <div
                                            key={detail.id}
                                            className="relative h-56 sm:h-64 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700"
                                            onMouseEnter={() => !isMobile && setHoveredCard(detail.id)}
                                            onMouseLeave={() => !isMobile && setHoveredCard(null)}
                                            onClick={(e) => isMobile && toggleCardOptions(detail.id, e)}
                                        >
                                            {/* Front of ID Card */}
                                            <div className={`absolute inset-0 w-full h-full bg-white dark:bg-gray-800 transition-all duration-300 ${hoveredCard === detail.id && !isMobile ? 'opacity-0' : 'opacity-100'}`}>
                                                {/* Card Header with gradient */}
                                                <div className={`h-16 sm:h-20 bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-between px-3 sm:px-4`}>
                                                    <div className="flex items-center flex-1 min-w-0">
                                                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                                                            <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h2 className="text-white text-sm sm:text-lg font-semibold truncate">{detail.name}</h2>
                                                            {/* Display Categories - Hidden on mobile if needed */}
                                                            <div className="hidden sm:flex items-center space-x-2 mt-1">
                                                                {detail.categories && detail.categories.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {renderCategories(detail.categories, categories)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center flex-shrink-0">
                                                        {detail.favourite && !hideFavoriteIcon && (
                                                            <div className="text-yellow-300 mr-1 sm:mr-2">
                                                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                                </svg>
                                                            </div>
                                                        )}

                                                        {/* Mobile menu icon */}
                                                        {isMobile && (
                                                            <button
                                                                onClick={(e) => toggleCardOptions(detail.id, e)}
                                                                className="text-white p-1 rounded-full hover:bg-white/10 focus:outline-none"
                                                            >
                                                                <EllipsisVerticalIcon className="h-5 w-5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* ID Card Body */}
                                                <div className="px-3 sm:px-4 py-3 sm:py-4">
                                                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm mt-1 sm:mt-2">
                                                        {detail.encrypted_first_name && (
                                                            <div className="flex">
                                                                <span className="text-gray-500 dark:text-gray-400 w-12 sm:w-16 flex-shrink-0">Name:</span>
                                                                <span className="text-gray-800 dark:text-gray-200 truncate">{fullName || ''}</span>
                                                            </div>
                                                        )}

                                                        {detail.encrypted_email_address && (
                                                            <div className="flex">
                                                                <span className="text-gray-500 dark:text-gray-400 w-12 sm:w-16 flex-shrink-0">Email:</span>
                                                                <span className="text-gray-800 dark:text-gray-200 truncate">{detail.encrypted_email_address}</span>
                                                            </div>
                                                        )}
                                                        {detail.encrypted_phone && (
                                                            <div className="flex">
                                                                <span className="text-gray-500 dark:text-gray-400 w-12 sm:w-16 flex-shrink-0">Phone:</span>
                                                                <span className="text-gray-800 dark:text-gray-200 truncate">{detail.encrypted_phone}</span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Mobile categories display */}
                                                        {isMobile && detail.categories && detail.categories.length > 0 && (
                                                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {renderCategories(detail.categories, categories)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Simple footer with just the date */}
                                                <div className="absolute bottom-0 left-0 right-0 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 flex justify-end items-center border-t border-gray-200 dark:border-gray-600">
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        Last Updated:  <time dateTime={detail.updated_at}>
                                                            {new Date(detail.updated_at).toLocaleDateString('en-US', {
                                                                year: isMobile ? '2-digit' : 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                        </time>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Desktop hover controls */}
                                            {!isMobile && (
                                                <>
                                                    {isAdminView || ((!detail.is_owner && detail.permission_level && detail.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ?
                                                        <div className={`absolute inset-0 w-full h-full bg-gradient-to-r ${cardColor} flex flex-col items-center justify-center transition-all duration-300 ${hoveredCard === detail.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                                            <div className="absolute top-0 left-0 right-0 px-4 py-2 text-white">
                                                                <h3 className="text-lg font-semibold">{fullName}</h3>
                                                                <p className="text-xs opacity-80">Personal Information</p>
                                                            </div>

                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="w-3/4 py-2 px-4 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 mb-3"
                                                            >
                                                                <EyeIcon className="h-5 w-5" />
                                                                <span>View Details</span>
                                                            </button>
                                                            {isAdminView && (
                                                                <button
                                                                    onClick={() => handleClone(detail)}
                                                                    className="w-3/4 py-2 px-4 bg-green-500/20 hover:bg-green-500/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                                                                >
                                                                    <DocumentDuplicateIcon className="h-5 w-5" />
                                                                    <span>Clone</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                        :
                                                        <div className={`absolute inset-0 w-full h-full bg-gradient-to-r ${cardColor} flex flex-col items-center justify-center transition-all duration-300 ${hoveredCard === detail.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                                            <div className="absolute top-0 left-0 right-0 px-4 py-2 text-white">
                                                                <h3 className="text-lg font-semibold">{fullName}</h3>
                                                                <p className="text-xs opacity-80">Personal Information</p>
                                                            </div>

                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="w-3/4 py-2 px-4 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 mb-3"
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                                <span>Edit Details</span>
                                                            </button>
                                                            {((detail.is_owner) || (!detail.is_owner)) && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(detail.id, detail.is_owner ?? false)}
                                                                    className="w-3/4 py-2 px-4 bg-red-500/20 hover:bg-red-500/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    }
                                                </>
                                            )}

                                            {/* Mobile action menu */}
                                            {isMobile && activeCard === detail.id && (
                                                <div
                                                    className="absolute right-0 left-0 bottom-0 bg-white dark:bg-gray-800 backdrop-blur-sm rounded-b-lg z-10 p-3 shadow-lg"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ borderTop: '1px solid rgba(0,0,0,0.1)' }}
                                                >
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h4 className="font-medium text-gray-800 dark:text-white text-sm">Personal options</h4>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveCard(null);
                                                            }}
                                                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                                        >
                                                            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                                        </button>
                                                    </div>
                                                    {isAdminView || ((!detail.is_owner && detail.permission_level && detail.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ?
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                            >
                                                                <EyeIcon className="h-4 w-4" />
                                                                <span>View Details</span>
                                                            </button>
                                                            {isAdminView && (
                                                                <button
                                                                    onClick={() => handleClone(detail)}
                                                                    className="py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                                >
                                                                    <DocumentDuplicateIcon className="h-4 w-4" />
                                                                    <span>Clone</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                        :
                                                        <div className="grid grid-cols-1 gap-3">
                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                                <span>Edit Details</span>
                                                            </button>
                                                            {((detail.is_owner) || (!detail.is_owner && role_id !== 3)) && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(detail.id, detail.is_owner ?? false)}
                                                                    className="py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* List View with Sidebar */
                            <div className="flex h-[calc(100vh-180px)] bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {/* Left Sidebar - List */}
                                <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                            Personal Details ({sortedPersonalDetails.length})
                                        </h3>
                                    </div>
                                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {sortedPersonalDetails.map((detail) => {
                                            const fullName = `${detail.title || ''} ${detail.encrypted_first_name || ''} ${detail.encrypted_last_name || ''}`.trim();
                                            return (
                                                <div
                                                    key={detail.id}
                                                    onClick={() => setSelectedPersonal(detail)}
                                                    className={`p-4 cursor-pointer transition-colors duration-150 ${selectedPersonal?.id === detail.id
                                                            ? 'bg-primary-50 dark:bg-primary-900/30 border-r-2 border-primary-500'
                                                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                                                        }`}
                                                >
                                                    <div className="flex items-center space-x-3">
                                                        <div className="flex-shrink-0">
                                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedPersonal?.id === detail.id
                                                                    ? 'bg-primary-100 dark:bg-primary-800'
                                                                    : 'bg-gray-100 dark:bg-gray-700'
                                                                }`}>
                                                                <UserIcon className={`w-5 h-5 ${selectedPersonal?.id === detail.id
                                                                        ? 'text-primary-600 dark:text-primary-300'
                                                                        : 'text-gray-500 dark:text-gray-400'
                                                                    }`} />
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between">
                                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                                    {detail.name || fullName || 'Unnamed Detail'}
                                                                </p>
                                                                {!detail.is_owner && (
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                                                        Shared
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="mt-1">
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                    {detail.encrypted_email_address || detail.encrypted_phone || 'No contact info'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Right Panel - Details */}
                                <div className="flex-1 flex flex-col">
                                    {selectedPersonal ? (
                                        <>
                                            {/* Header */}
                                            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                                                            <UserIcon className="w-6 h-6 text-white" />
                                                        </div>
                                                        <div>
                                                            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                                                {selectedPersonal.name || `${selectedPersonal.title || ''} ${selectedPersonal.encrypted_first_name || ''} ${selectedPersonal.encrypted_last_name || ''}`.trim() || 'Unnamed Detail'}
                                                            </h2>
                                                            <div className="flex items-center space-x-2 mt-1">
                                                                {!selectedPersonal.is_owner && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                                                        Shared Detail
                                                                    </span>
                                                                )}
                                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                    Updated {new Date(selectedPersonal.updated_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        {isAdminView || ((!selectedPersonal.is_owner && selectedPersonal.permission_level && selectedPersonal.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ? (
                                                            <button
                                                                onClick={() => handleEdit(selectedPersonal.id, selectedPersonal.is_shared, selectedPersonal.shared_key)}
                                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                                                            >
                                                                <EyeIcon className="w-4 h-4 mr-2" />
                                                                View
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    onClick={() => handleEdit(selectedPersonal.id, selectedPersonal.is_shared, selectedPersonal.shared_key)}
                                                                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                                                                >
                                                                    <PencilIcon className="w-4 h-4 mr-2" />
                                                                    Edit
                                                                </button>
                                                                {((selectedPersonal.is_owner) || (!selectedPersonal.is_owner && role_id !== 3)) && (
                                                                    <button
                                                                        onClick={() => handleDeleteClick(selectedPersonal.id, selectedPersonal.is_owner ?? false)}
                                                                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                                                                    >
                                                                        <TrashIcon className="w-4 h-4 mr-2" />
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 p-6 overflow-y-auto">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {/* Basic Information */}
                                                    <div className="space-y-4">
                                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                                                            Personal Information
                                                        </h3>

                                                        {selectedPersonal.encrypted_first_name && (
                                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            Full Name
                                                                        </label>
                                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                                            {`${selectedPersonal.title || ''} ${selectedPersonal.encrypted_first_name || ''} ${selectedPersonal.encrypted_last_name || ''}`.trim()}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleCopyItem('Full Name', `${selectedPersonal.title || ''} ${selectedPersonal.encrypted_first_name || ''} ${selectedPersonal.encrypted_last_name || ''}`.trim())}
                                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                    >
                                                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {selectedPersonal.encrypted_email_address && (
                                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            Email Address
                                                                        </label>
                                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                                                                            <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                                                                            {selectedPersonal.encrypted_email_address}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleCopyItem('Email', selectedPersonal.encrypted_email_address || '')}
                                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                    >
                                                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {selectedPersonal.encrypted_phone && (
                                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            Phone Number
                                                                        </label>
                                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center">
                                                                            <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                                                                            {selectedPersonal.encrypted_phone}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleCopyItem('Phone', selectedPersonal.encrypted_phone || '')}
                                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                    >
                                                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {selectedPersonal.encrypted_address_1 && (
                                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex-1">
                                                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            Address
                                                                        </label>
                                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                                            {selectedPersonal.encrypted_address_1}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleCopyItem('Address', selectedPersonal.encrypted_address_1 || '')}
                                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                    >
                                                                        <ClipboardDocumentIcon className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Additional Information */}
                                                    <div className="space-y-4">
                                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                                                            Additional Details
                                                        </h3>

                                                        {selectedPersonal.categories && selectedPersonal.categories.length > 0 && (
                                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                    Categories
                                                                </label>
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {renderCategories(selectedPersonal.categories, categories)}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                Created
                                                            </label>
                                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                                {new Date(selectedPersonal.created_at).toLocaleString()}
                                                            </p>
                                                        </div>

                                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                Last Updated
                                                            </label>
                                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                                {new Date(selectedPersonal.updated_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex-1 flex items-center justify-center">
                                            <div className="text-center">
                                                <UserIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                                                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No detail selected</h3>
                                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                    Choose a personal detail from the list to view details
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Personal Detail"
                message="Are you sure you want to delete this personal detail? This action cannot be undone."
                confirmText="Delete"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default PersonalDetailsList; 